import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type {
  GetRegisteroppforingPortalResponse,
  Register,
  RegisterOppforingRequest,
  RegisterOppforingResponse,
} from '../../types/helsenorge.helseregistre.internalapi.entities.entities';
import { InfoMelding, type WarnMelding } from '../../types/helsenorge.helseregistre.internalapi.entities.enums';
import type { RootState } from '../store';

import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';
import { get } from '@helsenorge/framework-utils/hn-proxy-service';

import { sortRegistre } from './registre-utils';

export interface RegistreState {
  loading: boolean;
  loaded: boolean;
  error: boolean;
  registerliste: Array<Register>;
  warn: WarnMelding | null;
  info: InfoMelding | null;
  retryInnsynOrkestrator: boolean;
}

export const initialState: RegistreState = {
  registerliste: [],
  loading: false,
  loaded: false,
  error: false,
  warn: null,
  info: null,
  retryInnsynOrkestrator: false,
};

export const fetchRegisterliste = createAsyncThunk(
  'helseregistre/GET',
  ({ retryInnsynOrkestrator: RetryInnsynOrkestrator }: Partial<RegisterOppforingRequest>) =>
    get<RegisterOppforingResponse>('helseregistre', 'api/v1/Registeroppforing', {
      RetryInnsynOrkestrator: RetryInnsynOrkestrator ?? false,
    }),
  {
    condition: (args, { getState }) => {
      const state = (getState() as RootState).registre;
      return !state.loading && (!state.loaded || (args.retryInnsynOrkestrator && state.retryInnsynOrkestrator));
    },
  }
);

export const fetchRegisterlistePortal = createAsyncThunk(
  'helseregistre/GET/Portal',
  () => get<GetRegisteroppforingPortalResponse>('helseregistre', 'api/v1/GetRegisteroppforingPortal'),
  {
    condition: (_args, { getState }) => {
      const state = (getState() as RootState).registre;
      return !state.loading && !isAuthorized() && !state.loaded;
    },
  }
);

const registerSlice = createSlice({
  name: 'registre',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchRegisterliste.pending, state => {
        state.loading = true;
      })
      .addCase(fetchRegisterliste.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        if (action.payload && Object.keys(action.payload).length > 0) {
          const { registre: Registre, warn: Warn, info: Info } = action.payload;
          state.registerliste = Registre ?? [];
          state.warn = Warn;
          state.info = Info;
          state.retryInnsynOrkestrator = Info === InfoMelding.RegistreIkkeKomplett;
        }
      })
      .addCase(fetchRegisterliste.rejected, state => {
        state.error = true;
        state.loading = false;
      })
      .addCase(fetchRegisterlistePortal.pending, state => {
        state.loading = true;
      })
      .addCase(fetchRegisterlistePortal.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && Object.keys(action.payload).length > 0) {
          const payload = action.payload;
          state.registerliste = payload.registre ? sortRegistre(payload.registre) : [];
        }
      })
      .addCase(fetchRegisterlistePortal.rejected, state => {
        state.error = true;
        state.loading = false;
      });
  },
});

export default registerSlice.reducer;
